import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Sun, Shield, Sparkles, Award, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { BBN_PHOTOS } from "@/lib/images";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";

/**
 * Hub-sida för söktermen "3m fönsterfilm" — bred brand-query som ska
 * fånga upp all trafik som söker efter 3M-fönsterfilm generellt och
 * dela ut den vidare till rätt undersida (Prestige, Ultra, säkerhets-
 * eller dekorfilm).
 */

const categories = [
  {
    icon: Sun,
    title: "3M solfilm — Prestige-serien",
    desc: "Prestige är en klar, nanokeramisk solfilm utan metaller. Invändigt använder vi Prestige 70, utvändigt Prestige 40 Exterior, 70 Exterior och 90 Exterior. Filmen ger hög värmereduktion, 99 % UV-skydd och upp till 15 års garanti.",
    to: "/3m-prestige" as const,
    links: [
      { label: "3M Prestige 70", to: "/3m-prestige-70" as const },
      { label: "3M Prestige 40 Exterior", to: "/3m-prestige-40-exterior" as const },
    ],
  },
  {
    icon: Shield,
    title: "3M säkerhetsfilm — Safety & Ultra",
    desc: "Splitterskyddande polyesterfilm som håller ihop krossat glas vid inbrott, explosion eller olycka. I sortimentet finns Safety S40–S140 och Ultra S600/S800 för extra skydd.",
    to: "/sakerhetsfilm" as const,
    links: [
      { label: "3M Ultra säkerhetsfilm", to: "/3m-ultra-sakerhetsfilm" as const },
      { label: "Säkerhetsfilm + solfilm-kombo", to: "/3m-sakerhetsfilm-med-solfilm" as const },
    ],
  },
  {
    icon: Sparkles,
    title: "3M dekorfilm — Fasara & Crystal",
    desc: "Med frostade och mönstrade dekorfilmer får du insynsskydd och kan dela in rum eller sätta logotyper på glas. Här finns designer A–I och Crystal-serien.",
    to: "/dekorfilm" as const,
    links: [],
  },
];

export const Route = createFileRoute("/3m-fonsterfilm")({
  head: () => ({
    meta: [
      { title: "3M fönsterfilm — solfilm, säkerhetsfilm & dekorfilm | Auktoriserad återförsäljare BBN Nordic" },
      {
        name: "description",
        content:
          "3M fönsterfilm i Sverige: Prestige-solfilm, Ultra säkerhetsfilm och Fasara dekorfilm. Auktoriserad 3M Authorized Dealer sedan 2008. Datablad, priser och kostnadsfri offert.",
      },
      { property: "og:title", content: "3M fönsterfilm — auktoriserad återförsäljare i Sverige" },
      {
        property: "og:description",
        content:
          "Solfilm, säkerhetsfilm och dekorfilm från 3M — installerat av 3M Authorized Dealer BBN Nordic.",
      },
      { property: "og:url", content: "https://bbnnordic.se/3m-fonsterfilm" },
      { property: "og:image", content: BBN_PHOTOS.prestigeHero },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/3m-fonsterfilm" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "3M fönsterfilm",
          description: "Översikt över 3M:s fönsterfilmssortiment i Sverige — sol-, säkerhets- och dekorfilm.",
          url: "https://bbnnordic.se/3m-fonsterfilm",
          about: [
            { "@type": "Brand", name: "3M" },
            { "@type": "Product", name: "3M Prestige" },
            { "@type": "Product", name: "3M Ultra Safety" },
            { "@type": "Product", name: "3M Fasara" },
          ],
          provider: { "@id": "https://bbnnordic.se/#organization" },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="3M FÖNSTERFILM"
        title="3M fönsterfilm i Sverige — sol, säkerhet & dekor från 3M Authorized Dealer"
        subtitle="BBN Nordic AB är auktoriserad 3M-installatör sedan 2008. Vi levererar och monterar hela 3M:s sortiment av fönsterfilm, däribland Prestige, Ultra Safety och Fasara, med spårbara batchnummer och 3M:s originalgaranti."
        image={BBN_PHOTOS.prestigeHero}
        breadcrumbs={[{ label: "3M fönsterfilm" }]}
        landingConversion
      />
      <PostHeroConversion />

      {/* Auktoriserad-banner */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-card/80 to-card/40 p-6 md:p-8 flex items-start gap-4">
              <div className="rounded-xl bg-gold/15 p-3 shrink-0"><Award size={24} className="text-gold" /></div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">3M Authorized Dealer</p>
                <h2 className="mt-2 font-display text-2xl md:text-3xl text-foreground">
                  Endast auktoriserade installatörer får utfärda 3M:s originalgaranti.
                </h2>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  Vi köper all 3M-film direkt från 3M och varje film har spårbara batch­nummer. Du får
                  3M:s fulla produktgaranti (upp till 15 år på Prestige, 10 år på Ultra Safety) och
                  lämnar dessutom 10 års garanti på vårt installationsarbete.
                </p>
                <Link to="/auktoriserad-installator" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gold-bright hover:gap-3 transition-all">
                  Läs mer om auktoriserad installation <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Kategorier */}
      <section className="py-12 md:py-20 bg-navy-light border-y border-border">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">3M-SORTIMENTET</p>
            <h2 className="mt-3 text-3xl md:text-4xl max-w-3xl">
              Vilken 3M fönsterfilm behöver du?
            </h2>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {categories.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} delay={i * 0.08}>
                  <article className="glass p-7 h-full flex flex-col">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded bg-gold/15 text-gold">
                      <Icon size={22} />
                    </span>
                    <h3 className="mt-5 font-display text-xl text-foreground">{c.title}</h3>
                    <p className="mt-3 text-sm text-muted-foreground font-light flex-1 leading-relaxed">{c.desc}</p>
                    {c.links.length > 0 && (
                      <ul className="mt-4 space-y-1">
                        {c.links.map((l) => (
                          <li key={l.to}>
                            <Link to={l.to} className="text-sm text-gold-bright hover:underline flex items-center gap-1">
                              <Check size={12} /> {l.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link to={c.to} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:gap-3 transition-all">
                      Se hela kategorin <ArrowRight size={14} />
                    </Link>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl">Osäker på vilken 3M-film som passar?</h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              Vi mäter dina fönster och filmar en demo mot din egen ruta så att du kan se skillnaden. Du får en skriftlig offert med fast pris. Det kostar inget.
            </p>
            <Link to="/offert" className="mt-6 btn-glow inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold">
              Få kostnadsfri offert <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
